import type { Flashcard } from '../types';
import { Edit, Trash, RotateCcw, Tag } from 'lucide-react';

interface FlashcardCardProps {
  card: Flashcard;
  onEdit?: (card: Flashcard) => void;
  onDelete?: (cardId: string) => void;
  onFlip?: (card: Flashcard) => void;
  compact?: boolean;
  showActions?: boolean;
  className?: string;
}

export const FlashcardCard: React.FC<FlashcardCardProps> = ({
  card,
  onEdit,
  onDelete,
  onFlip,
  compact = false,
  showActions = true,
  className = '',
}) => {
  const difficultyColor = {
    easy: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    hard: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}
    >
      <div className={`p-4 ${compact ? 'space-y-2' : 'space-y-3'}`}>
        {/* Front */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Question
          </p>
          <p className="text-gray-900 dark:text-gray-100 text-sm leading-relaxed">
            {card.front}
          </p>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-100 dark:border-gray-700" />

        {/* Back */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Answer
          </p>
          <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
            {card.back}
          </p>
        </div>

        {/* Hint */}
        {card.hint && !compact && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5">
            <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">Hint</p>
            <p className="text-sm text-blue-700 dark:text-blue-400">{card.hint}</p>
          </div>
        )}

        {/* Tags & Meta */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2 flex-wrap">
            {card.tags.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500">
                <Tag className="w-3 h-3" />
                <span>{card.tags.slice(0, 3).join(', ')}</span>
              </div>
            )}
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                difficultyColor[card.difficulty]
              }`}
            >
              {card.difficulty}
            </span>
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex items-center gap-1">
              {onFlip && (
                <button
                  onClick={() => onFlip(card)}
                  className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  title="Study this card"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              {onEdit && (
                <button
                  onClick={() => onEdit(card)}
                  className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  title="Edit card"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(card.id)}
                  className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                  title="Delete card"
                >
                  <Trash className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlashcardCard;
